import {useEffect} from "react";
import {useParams} from "react-router-dom";
import {
  useGetExamConfigQuery,
  useUpdateProblemsetFiltersMutation,
} from "../store/api/examConfigApiSlice";
import {useExamPageNavigate} from "../pages/exam/common/navigation/useExamPageNavigate";
import {ExamPageSection} from "../pages/exam/common/navigation/routes";
import {
  AttemptStatusType,
  FilterType,
  ProblemDifficultyType,
  ProblemType,
} from "../config/enums";
import {QuestionCategoryType} from "../features/problemset-table-v2/data/question-category-data";

export interface HandleProblemsetFilterUpdatesProps {
  [FilterType.FEATURE]?: QuestionCategoryType;
  [FilterType.ATTEMPT_STATUS]?: AttemptStatusType[];
  [FilterType.DIFFICULTY]?: ProblemDifficultyType[];
  [FilterType.PATTERN]?: ProblemType[];
  [FilterType.PAGE]?: number;
}

export function useProblemsetSync(isProblemDialogRoute: boolean = false) {
  const examPageNavigate = useExamPageNavigate();

  const {subjectId: subjectIdParam, chapterId: chapterIdParam} = useParams();
  const {data: examConfigData} = useGetExamConfigQuery();
  const [updateProblemsetFilters] = useUpdateProblemsetFiltersMutation();

  const selectedSubject = examConfigData?.problemsetFilters.subjects.find(
    subject => subject.isSelected,
  );
  const selectedSubjectId = selectedSubject?.subjectId;
  const selectedChapter = selectedSubject?.chapters.find(
    chapter => chapter.isSelected,
  );
  const selectedChapterId = selectedChapter?.chapterId;

  // Handle URL parameter changes and filter synchronization
  useEffect(() => {
    if (isProblemDialogRoute) return;
    if (!subjectIdParam || !chapterIdParam) return;

    updateProblemsetFilters({
      subjectId: subjectIdParam,
      chapterId: chapterIdParam,
    });
  }, [
    subjectIdParam,
    chapterIdParam,
    isProblemDialogRoute,
    updateProblemsetFilters,
  ]);

  function handleProblemsetFilterUpdates({
    [FilterType.FEATURE]: feature,
    [FilterType.PAGE]: page,
    [FilterType.ATTEMPT_STATUS]: attemptStatus,
    [FilterType.DIFFICULTY]: difficulty,
    [FilterType.PATTERN]: pattern,
  }: HandleProblemsetFilterUpdatesProps) {
    if (!selectedSubjectId || !selectedChapterId) return;
    updateProblemsetFilters({
      subjectId: selectedSubjectId,
      chapterId: selectedChapterId,
      [FilterType.FEATURE]: feature,
      [FilterType.PAGE]: page,
      [FilterType.ATTEMPT_STATUS]: attemptStatus,
      [FilterType.DIFFICULTY]: difficulty,
      [FilterType.PATTERN]: pattern,
    });
  }

  return {
    selectedSubject,
    selectedChapter,
    handleProblemsetFilterUpdates,
  };
}
